import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import {
  ApplicationForm,
  blankForm,
  blankLocation,
  blankPrincipal,
  blankTeamMember,
  isPrepay,
  applicableSteps,
  SECTIONS,
  BUSINESS_TYPES,
  ENTITY_TYPES,
  CREDIT_LINES,
  TONS_RANGES,
  HEARD_OPTIONS,
  TEAM_ROLES,
  RESALE_SELLER_TYPES,
  US_STATES,
  creditLineLabel,
  totalOwnershipPercent,
} from '../../features/customer-application/applicationForm/types';
import {
  removeLocationAt,
  effectiveLocationAddress,
  sameAsPreviousLabel,
} from '../../features/customer-application/applicationForm/locations';
import {
  validateStep,
  Errors,
} from '../../features/customer-application/applicationForm/validation';
import { hydrateForm } from '../../features/customer-application/applicationForm/hydrate';
import {
  useCreateApplicantAccount,
  useSendCode,
  useVerifyCode,
  useTradeReferenceUpload,
  useSubmitApplication,
  useMyApplication,
  useMyApplicationFull,
  useApplicationProducts,
} from '../../features/customer-application/api';
import { useRecaptcha } from '../../features/customer-application/useRecaptcha';
import AddressAutocomplete, {
  ParsedAddress,
} from '../../components/AddressAutocomplete/AddressAutocomplete';
import { MultiSelectDropdown } from '../../components/MultiSelectDropdown/MultiSelectDropdown';
import './apply.scss';

type Patch = Partial<ApplicationForm>;

export default function ApplyPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [stage, setStage] = useState<'intro' | 'wizard'>('intro');
  const [form, setForm] = useState<ApplicationForm>(blankForm());
  const [stepIndex, setStepIndex] = useState(0);
  const [errors, setErrors] = useState<Errors>({});
  const [accountCreated, setAccountCreated] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  // Edit-and-resubmit mode for a NEEDS_INFO application: the account already
  // exists and the form is reloaded from the server.
  const [editMode, setEditMode] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [needsInfoMessage, setNeedsInfoMessage] = useState('');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [accountError, setAccountError] = useState('');

  const createAccount = useCreateApplicantAccount();
  const sendCode = useSendCode();
  const verifyCode = useVerifyCode();
  const tradeRefUpload = useTradeReferenceUpload();
  const submitApp = useSubmitApplication();
  const { execute: executeRecaptcha } = useRecaptcha();
  const { data: productsData } = useApplicationProducts(accountCreated);
  const myApp = useMyApplication(Boolean(user));
  const appStatus = myApp.data?.application?.status;
  const isNeedsInfo = appStatus === 'NEEDS_INFO';
  const fullApp = useMyApplicationFull(isNeedsInfo);

  // Route a logged-in applicant who already has an application: NEEDS_INFO
  // reopens the wizard to edit; any other submitted/closed status → status page.
  useEffect(() => {
    if (!appStatus || appStatus === 'NEEDS_INFO' || appStatus === 'DRAFT') return;
    navigate('/apply/status', { replace: true });
  }, [appStatus, navigate]);

  // Load a NEEDS_INFO application into the wizard once.
  useEffect(() => {
    if (!isNeedsInfo || hydrated) return;
    const full = fullApp.data?.application;
    if (!full) return;
    setForm(hydrateForm(full));
    setNeedsInfoMessage(full.needsInfoMessage ?? '');
    setEditMode(true);
    setAccountCreated(true);
    setEmailVerified(true);
    setStage('wizard');
    setStepIndex(0);
    setHydrated(true);
  }, [isNeedsInfo, hydrated, fullApp.data]);

  // In edit mode the account already exists and the email is verified, so skip
  // the account-creation and verification steps.
  const steps = useMemo(
    () => applicableSteps(form).filter((s) => !editMode || (s.id !== 'owner' && s.id !== 'verify')),
    [form, editMode]
  );
  const current = steps[Math.min(stepIndex, steps.length - 1)];
  const sectionIndex = SECTIONS.indexOf(current?.section ?? '');

  const update = (patch: Patch) => setForm((f) => ({ ...f, ...patch }));
  const clearErr = (key: string) =>
    setErrors((e) => {
      if (!(key in e)) return e;
      const next = { ...e };
      delete next[key];
      return next;
    });

  const goNext = () => {
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
    setErrors({});
    window.scrollTo({ top: 0 });
  };
  const goBack = () => {
    setStepIndex((i) => Math.max(i - 1, 0));
    setErrors({});
    window.scrollTo({ top: 0 });
  };

  // Send the verification code once when the verify step is first shown.
  const codeRequested = useRef(false);
  useEffect(() => {
    if (current?.id === 'verify' && accountCreated && !emailVerified && !codeRequested.current) {
      codeRequested.current = true;
      sendCode.mutate();
    }
  }, [current, accountCreated, emailVerified, sendCode]);

  async function handleContinue() {
    if (!current) return;

    // Owner step: validate, then create the Firebase account + DRAFT application.
    if (current.id === 'owner' && !accountCreated) {
      const e = validateStep('owner', form);
      if (Object.keys(e).length) return setErrors(e);
      setAccountError('');
      try {
        await createAccount.mutateAsync(form);
        setAccountCreated(true);
        goNext();
      } catch (err: any) {
        if (err?.code === 'EMAIL_IN_USE') {
          setAccountError(
            'You already have an account with this email. Sign in to continue your application.'
          );
        } else if (err?.code === 'CUSTOMER_EXISTS') {
          const s = err?.customerStatus;
          setAccountError(
            s === 'ARCHIVED' || s === 'REJECTED'
              ? 'An account for this email already exists with DeFeo but isn’t active. Please contact your DeFeo rep to reactivate it.'
              : 'This email is already registered with DeFeo. Please sign in instead of starting a new application.'
          );
        } else {
          setAccountError(
            'We could not create your account. Please check your details and try again.'
          );
        }
      }
      return;
    }

    // Verify step: must be verified to advance.
    if (current.id === 'verify') {
      if (!emailVerified) {
        setCodeError('');
        try {
          const res = await verifyCode.mutateAsync(code.trim());
          if (res.verified) {
            setEmailVerified(true);
            goNext();
          }
        } catch (err: any) {
          const c = err?.response?.data?.error;
          setCodeError(
            c === 'CODE_EXPIRED'
              ? 'That code expired. Resend a new one.'
              : c === 'TOO_MANY_ATTEMPTS'
                ? 'Too many attempts. Resend a new code.'
                : "That code doesn't match. Check your email or resend it."
          );
        }
        return;
      }
      goNext();
      return;
    }

    // Review step: submit.
    if (current.id === 'review') {
      const e = validateStep('review', form);
      if (Object.keys(e).length) return setErrors(e);
      try {
        const token = await executeRecaptcha('submit_application');
        await submitApp.mutateAsync({ form, recaptchaToken: token });
        navigate('/apply/status', { replace: true });
      } catch (err: any) {
        const c = err?.response?.data?.error;
        setErrors({
          submit:
            c === 'RECAPTCHA_FAILED'
              ? "We couldn't verify you're human. Please try again."
              : c === 'APPLICATION_ALREADY_ON_FILE' || c === 'ALREADY_SUBMITTED'
                ? 'An application is already on file for this account.'
                : 'Something went wrong submitting your application. Please try again.',
        });
      }
      return;
    }

    // Every other step: validate and advance.
    const e = validateStep(current.id, form);
    if (Object.keys(e).length) return setErrors(e);
    goNext();
  }

  const products = productsData?.products ?? [];

  if (authLoading) return null;
  // A returning NEEDS_INFO applicant: wait for the reload instead of flashing the hero.
  if (isNeedsInfo && !hydrated) return null;

  // ---- intro hero ----
  if (stage === 'intro') {
    return (
      <div className="apply-intro">
        <div className="apply-intro__card">
          <span className="apply-eyebrow">DeFeo Materials</span>
          <h1>Open a DeFeo Materials account</h1>
          <p>
            Apply for credit, set up your delivery locations and team, and start ordering in BMP
            once you're approved.
          </p>
          <button className="apply-btn apply-btn--primary" onClick={() => setStage('wizard')}>
            Start your application
          </button>
          <p className="apply-muted apply-intro__note">
            Takes about 10 minutes. Have ready: your EIN, each principal's home address and SSN,
            your bank name and account number, three trade references, and your state resale/tax
            registration number if you're tax-exempt.
          </p>
        </div>
      </div>
    );
  }

  const busy =
    createAccount.isPending ||
    verifyCode.isPending ||
    submitApp.isPending ||
    tradeRefUpload.isPending;

  return (
    <div className="apply-wiz">
      {editMode && (
        <div className="apply-banner apply-banner--warn">
          <b>Your rep asked for more information.</b>
          {needsInfoMessage ? <p style={{ margin: '4px 0 0' }}>{needsInfoMessage}</p> : null}
          <p style={{ margin: '4px 0 0' }}>
            Update anything below and resubmit. Your SSN and bank account stay on file — leave those
            blank to keep them.
          </p>
        </div>
      )}
      {/* progress */}
      <div className="apply-prog" aria-label="Progress">
        {SECTIONS.map((s, i) => (
          <div key={s} className={i < sectionIndex ? 'done' : i === sectionIndex ? 'cur' : ''}>
            <div className="apply-prog__seg" />
            <span>{s}</span>
          </div>
        ))}
      </div>

      <form
        className="apply-card"
        noValidate
        onSubmit={(ev) => {
          ev.preventDefault();
          handleContinue();
        }}
      >
        <div className="apply-card__head">
          <span className="apply-eyebrow">
            {current.section} · Step {stepIndex + 1} of {steps.length}
          </span>
          <h2>{current.title}</h2>
          {current.help && <p className="apply-help">{current.help}</p>}
        </div>

        <StepBody
          step={current.id}
          form={form}
          errors={errors}
          update={update}
          clearErr={clearErr}
          products={products}
          code={code}
          setCode={setCode}
          codeError={codeError}
          accountError={accountError}
          emailVerified={emailVerified}
          onResend={() => {
            setCodeError('');
            sendCode.mutate();
          }}
          onTradeRefFile={async (file) => {
            update({ tradeReferenceFileName: file.name });
            try {
              await tradeRefUpload.mutateAsync(file);
            } catch {
              update({ tradeReferenceFileName: '' });
            }
          }}
        />

        {errors.submit && <div className="apply-err apply-err--block">{errors.submit}</div>}

        <div className="apply-nav">
          <div>
            {stepIndex > 0 && (
              <button type="button" className="apply-btn" onClick={goBack} disabled={busy}>
                Back
              </button>
            )}
          </div>
          <button type="submit" className="apply-btn apply-btn--primary" disabled={busy}>
            {busy
              ? 'Working…'
              : current.id === 'review'
                ? editMode
                  ? 'Resubmit application'
                  : 'Submit application'
                : current.id === 'team' && !form.teamMembers.length
                  ? 'Skip for now'
                  : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step bodies
// ---------------------------------------------------------------------------
interface BodyProps {
  step: string;
  form: ApplicationForm;
  errors: Errors;
  update: (p: Patch) => void;
  clearErr: (k: string) => void;
  products: any[];
  code: string;
  setCode: (v: string) => void;
  codeError: string;
  accountError: string;
  emailVerified: boolean;
  onResend: () => void;
  onTradeRefFile: (f: File) => void;
}

function Field(props: {
  label: string;
  id: keyof ApplicationForm;
  form: ApplicationForm;
  errors: Errors;
  update: (p: Patch) => void;
  clearErr: (k: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  width?: number;
}) {
  const {
    label,
    id,
    form,
    errors,
    update,
    clearErr,
    type = 'text',
    required,
    placeholder,
    width,
  } = props;
  const err = errors[id as string];
  return (
    <div className={`apply-f${width ? ' w' + width : ''}`}>
      <label htmlFor={`f_${id}`}>
        {label}
        {required && <span className="apply-req"> *</span>}
      </label>
      <input
        id={`f_${id}`}
        type={type}
        value={(form[id] as string) ?? ''}
        placeholder={placeholder}
        className={err ? 'bad' : ''}
        autoComplete="off"
        onChange={(ev) => {
          update({ [id]: ev.target.value } as Patch);
          clearErr(id as string);
        }}
      />
      {err && <div className="apply-err">{err}</div>}
    </div>
  );
}

function SelectField(props: {
  label: string;
  id: keyof ApplicationForm;
  form: ApplicationForm;
  errors: Errors;
  update: (p: Patch) => void;
  clearErr: (k: string) => void;
  options: { value: string; label: string }[];
  required?: boolean;
  width?: number;
}) {
  const { label, id, form, errors, update, clearErr, options, required, width } = props;
  const err = errors[id as string];
  return (
    <div className={`apply-f${width ? ' w' + width : ''}`}>
      <label htmlFor={`f_${id}`}>
        {label}
        {required && <span className="apply-req"> *</span>}
      </label>
      <select
        id={`f_${id}`}
        value={(form[id] as string) ?? ''}
        className={err ? 'bad' : ''}
        onChange={(ev) => {
          update({ [id]: ev.target.value } as Patch);
          clearErr(id as string);
        }}
      >
        <option value="">Choose…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {err && <div className="apply-err">{err}</div>}
    </div>
  );
}

const stateOptions = US_STATES.map((s) => ({ value: s, label: s }));

// Google-autocomplete street-address field: on pick, fills line1 and hands back
// the parsed city/state/zip. Falls back to a plain input with no Maps key.
function AutoLine1(props: {
  label: string;
  value: string;
  onText: (v: string) => void;
  onPlace: (p: ParsedAddress) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  width?: number;
}) {
  const { label, value, onText, onPlace, error, required, disabled, width } = props;
  return (
    <div className={`apply-f${width ? ' w' + width : ''}`}>
      <label>
        {label}
        {required && <span className="apply-req"> *</span>}
      </label>
      <AddressAutocomplete
        value={value}
        onChange={(e) => onText(e.target.value)}
        onPlaceSelected={onPlace}
        disabled={disabled}
        placeholder="Start typing address…"
        className={`apply-autocomplete${error ? ' bad' : ''}`}
      />
      {error && <div className="apply-err">{error}</div>}
    </div>
  );
}

function ChoiceGroup(props: {
  value: string;
  options: { value: string; label: string }[];
  error?: string;
  onPick: (v: string) => void;
}) {
  return (
    <div>
      <div className="apply-choices" role="radiogroup">
        {props.options.map((o) => (
          <button
            type="button"
            key={o.value}
            role="radio"
            aria-checked={props.value === o.value}
            className={`apply-choice${props.value === o.value ? ' on' : ''}`}
            onClick={() => props.onPick(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
      {props.error && <div className="apply-err">{props.error}</div>}
    </div>
  );
}

function StepBody(p: BodyProps) {
  const { form, errors, update, clearErr } = p;
  const f = (id: keyof ApplicationForm, label: string, opts: any = {}) => (
    <Field
      label={label}
      id={id}
      form={form}
      errors={errors}
      update={update}
      clearErr={clearErr}
      {...opts}
    />
  );
  const sel = (id: keyof ApplicationForm, label: string, options: any[], opts: any = {}) => (
    <SelectField
      label={label}
      id={id}
      form={form}
      errors={errors}
      update={update}
      clearErr={clearErr}
      options={options}
      {...opts}
    />
  );

  switch (p.step) {
    case 'business-name':
      return (
        <div className="apply-fields">
          {f('legalName', 'Legal business name', { required: true })}
          {f('dba', 'Doing business as (if different)')}
        </div>
      );

    case 'business-type':
      return (
        <ChoiceGroup
          value={form.businessType}
          options={BUSINESS_TYPES}
          error={errors.businessType}
          onPick={(v) => {
            update({ businessType: v });
            clearErr('businessType');
          }}
        />
      );

    case 'business-entity':
      return (
        <div className="apply-fields">
          {sel('entityType', 'Entity type', ENTITY_TYPES, { required: true, width: 3 })}
          {f('yearIncorporated', 'Year incorporated', {
            required: true,
            width: 3,
            placeholder: 'e.g. 2014',
          })}
          {sel('stateOfIncorporation', 'State of incorporation', stateOptions, {
            required: true,
            width: 2,
          })}
          {f('ein', 'Federal EIN (or SSN if sole proprietor)', {
            required: true,
            width: 4,
            placeholder: '12-3456789',
          })}
        </div>
      );

    case 'business-office':
      return (
        <div className="apply-fields">
          <AutoLine1
            label="Street address"
            required
            value={form.officeLine1}
            error={errors.officeLine1}
            onText={(v) => {
              update({ officeLine1: v });
              clearErr('officeLine1');
            }}
            onPlace={(addr) => {
              update({
                officeLine1: addr.line1,
                officeCity: addr.city,
                officeState: addr.state,
                officeZip: addr.postalCode,
                // A fresh pick is authoritative — set the unit (subpremise) or clear a stale one.
                officeLine2: addr.line2,
              });
              clearErr('officeLine1');
              clearErr('officeCity');
              clearErr('officeState');
              clearErr('officeZip');
            }}
          />
          {f('officeLine2', 'Suite, unit or floor', { placeholder: 'e.g. Suite 205' })}
          {f('officeCity', 'City', { required: true, width: 3 })}
          {sel('officeState', 'State', stateOptions, { required: true, width: 1 })}
          {f('officeZip', 'ZIP', { required: true, width: 2 })}
          {f('officePhone', 'Business phone', { required: true, type: 'tel', width: 2 })}
          {f('officeFax', 'Fax', { type: 'tel', width: 2, placeholder: 'optional' })}
          {f('officeWebsite', 'Website', { width: 2, placeholder: 'optional' })}
          <div className="apply-check-row" style={{ gridColumn: 'span 6' }}>
            <label className="apply-check">
              <input
                type="checkbox"
                checked={form.officeIsPrimary}
                onChange={(e) => update({ officeIsPrimary: e.target.checked })}
              />
              <span>This is our primary address</span>
            </label>
            <label className="apply-check">
              <input
                type="checkbox"
                checked={form.officeIsHeadquarters}
                onChange={(e) => update({ officeIsHeadquarters: e.target.checked })}
              />
              <span>This is our headquarters</span>
            </label>
          </div>
        </div>
      );

    case 'owner':
      return (
        <>
          {p.accountError && (
            <div className="apply-banner apply-banner--warn">{p.accountError}</div>
          )}
          <div className="apply-fields">
            {f('ownerFirstName', 'First name', { required: true, width: 3 })}
            {f('ownerLastName', 'Last name', { required: true, width: 3 })}
            {f('ownerTitle', 'Title', { width: 3 })}
            {f('ownerMobile', 'Mobile phone', { required: true, type: 'tel', width: 3 })}
            {f('ownerEmail', 'Email (this is your login)', { required: true, type: 'email' })}
            {f('password', 'Create a password', { required: true, type: 'password', width: 3 })}
            {f('passwordConfirm', 'Confirm password', {
              required: true,
              type: 'password',
              width: 3,
            })}
            {sel('heardAboutUs', 'How did you hear about us?', HEARD_OPTIONS, {
              required: true,
              width: 3,
            })}
            {f('heardAboutUsDetail', 'Who referred you, or other details', {
              width: 3,
              placeholder: 'optional',
            })}
          </div>
        </>
      );

    case 'verify':
      return (
        <div className="apply-fields">
          <p className="apply-help" style={{ gridColumn: 'span 6', margin: 0 }}>
            We sent a 6-digit code to <b>{form.ownerEmail}</b>. Enter it to confirm your email.
          </p>
          {p.emailVerified ? (
            <div className="apply-banner apply-banner--ok" style={{ gridColumn: 'span 6' }}>
              Email verified
            </div>
          ) : (
            <>
              <div className="apply-f w3">
                <label htmlFor="f_code">Verification code</label>
                <input
                  id="f_code"
                  className={`apply-code${p.codeError ? ' bad' : ''}`}
                  inputMode="numeric"
                  maxLength={6}
                  value={p.code}
                  autoComplete="one-time-code"
                  onChange={(ev) => p.setCode(ev.target.value.replace(/\D/g, ''))}
                />
                {p.codeError && <div className="apply-err">{p.codeError}</div>}
              </div>
              <div className="apply-f" style={{ alignSelf: 'end' }}>
                <button type="button" className="apply-btn apply-btn--ghost" onClick={p.onResend}>
                  Resend code
                </button>
              </div>
            </>
          )}
        </div>
      );

    case 'billing': {
      // Reuse the account-owner details entered earlier. Checked state is derived
      // from the fields (no extra form state); editing any field unchecks it.
      const ownerName = `${form.ownerFirstName} ${form.ownerLastName}`.trim();
      const billingIsOwner =
        !!ownerName &&
        form.billingContact === ownerName &&
        form.billingEmail === form.ownerEmail &&
        form.billingPhone === form.ownerMobile;
      return (
        <>
          <label className="apply-check" style={{ marginBottom: 10 }}>
            <input
              type="checkbox"
              checked={billingIsOwner}
              onChange={(ev) => {
                if (ev.target.checked) {
                  update({
                    billingContact: ownerName,
                    billingEmail: form.ownerEmail,
                    billingPhone: form.ownerMobile,
                  });
                  ['billingContact', 'billingEmail', 'billingPhone'].forEach(clearErr);
                } else {
                  update({ billingContact: '', billingEmail: '', billingPhone: '' });
                }
              }}
            />
            <span>Billing contact is the same as me (the account owner)</span>
          </label>
          <div className="apply-fields">
            {f('billingContact', 'Billing contact', { required: true, width: 3 })}
            {f('billingPhone', 'Billing phone', { required: true, type: 'tel', width: 3 })}
            {f('billingEmail', 'Billing email', { required: true, type: 'email' })}
          </div>
        </>
      );
    }

    case 'locations':
      return <LocationsBody form={form} errors={errors} update={update} clearErr={clearErr} />;

    case 'credit':
      return (
        <div className="apply-fields">
          <div className="apply-f">
            <label>
              Credit line<span className="apply-req"> *</span>
            </label>
            <ChoiceGroup
              value={form.creditLine}
              options={CREDIT_LINES}
              error={errors.creditLine}
              onPick={(v) => {
                update({ creditLine: v });
                clearErr('creditLine');
              }}
            />
          </div>
          {sel('annualTonsRange', 'How many tons do you expect to buy per year?', TONS_RANGES, {
            required: true,
            width: 4,
          })}
        </div>
      );

    case 'principals':
      return <PrincipalsBody form={form} errors={errors} update={update} clearErr={clearErr} />;

    case 'bank':
      return (
        <div className="apply-fields">
          {f('bankName', 'Bank name', { required: true, width: 3 })}
          {f('bankPhone', 'Bank phone', { required: true, type: 'tel', width: 3 })}
          <AutoLine1
            label="Bank address"
            required
            value={form.bankLine1}
            error={errors.bankLine1}
            onText={(v) => {
              update({ bankLine1: v });
              clearErr('bankLine1');
            }}
            onPlace={(addr) => {
              update({
                bankLine1: addr.line1,
                bankCity: addr.city,
                bankState: addr.state,
                bankZip: addr.postalCode,
              });
              clearErr('bankLine1');
            }}
          />
          {f('bankContact', 'Bank contact', { width: 3 })}
          {sel(
            'bankAccountType',
            'Account type',
            [
              { value: 'Business checking', label: 'Business checking' },
              { value: 'Business savings', label: 'Business savings' },
              { value: 'Line of credit', label: 'Line of credit' },
            ],
            { width: 3 }
          )}
        </div>
      );

    case 'references':
      return (
        <ReferencesBody
          form={form}
          errors={errors}
          update={update}
          clearErr={clearErr}
          onFile={p.onTradeRefFile}
        />
      );

    case 'bankruptcy':
      return (
        <>
          <ChoiceGroup
            value={form.hasBankruptcy}
            options={[
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' },
            ]}
            error={errors.hasBankruptcy}
            onPick={(v) => {
              update({ hasBankruptcy: v as 'yes' | 'no' });
              clearErr('hasBankruptcy');
            }}
          />
          {form.hasBankruptcy === 'yes' && (
            <div className="apply-f" style={{ marginTop: 12 }}>
              <label htmlFor="f_bk">
                Please explain<span className="apply-req"> *</span>
              </label>
              <textarea
                id="f_bk"
                rows={3}
                value={form.bankruptcyExplanation}
                className={errors.bankruptcyExplanation ? 'bad' : ''}
                onChange={(ev) => {
                  update({ bankruptcyExplanation: ev.target.value });
                  clearErr('bankruptcyExplanation');
                }}
              />
              {errors.bankruptcyExplanation && (
                <div className="apply-err">{errors.bankruptcyExplanation}</div>
              )}
            </div>
          )}
        </>
      );

    case 'team':
      return <TeamBody form={form} errors={errors} update={update} clearErr={clearErr} />;

    case 'products':
      return (
        <ProductsBody
          form={form}
          errors={errors}
          update={update}
          clearErr={clearErr}
          products={p.products}
        />
      );

    case 'tax':
      return (
        <>
          <ChoiceGroup
            value={form.taxExempt}
            options={[
              { value: 'yes', label: 'Yes, I have a resale certificate' },
              { value: 'no', label: 'No, charge sales tax' },
            ]}
            error={errors.taxExempt}
            onPick={(v) => {
              update({ taxExempt: v as 'yes' | 'no' });
              clearErr('taxExempt');
            }}
          />
          {form.taxExempt === 'yes' && (
            <>
              <p className="apply-help" style={{ marginTop: 12 }}>
                Enter your resale details and we'll generate the certificate for you — no upload or
                paperwork needed. Your name and address are pulled from this application.
              </p>
              <div className="apply-fields" style={{ marginTop: 4 }}>
                {sel('resaleCertState', 'Certificate state', stateOptions, {
                  required: true,
                  width: 2,
                })}
                {sel('resaleSellerType', 'Type of business', RESALE_SELLER_TYPES, {
                  required: true,
                  width: 2,
                })}
                {f('resaleRegistrationNumber', 'State registration / permit #', {
                  required: true,
                  width: 2,
                })}
                {f('resaleInBusinessOf', 'Engaged in the business of', {
                  placeholder: 'e.g. landscape supply',
                  width: 3,
                })}
                <div className="apply-f">
                  <label htmlFor="f_resaleDesc">
                    Description of materials purchased for resale
                    <span className="apply-req"> *</span>
                  </label>
                  <textarea
                    id="f_resaleDesc"
                    rows={2}
                    value={form.resaleDescription}
                    className={errors.resaleDescription ? 'bad' : ''}
                    placeholder="e.g. stone, gravel, sand and related aggregates"
                    onChange={(ev) => {
                      update({ resaleDescription: ev.target.value });
                      clearErr('resaleDescription');
                    }}
                  />
                  {errors.resaleDescription && (
                    <div className="apply-err">{errors.resaleDescription}</div>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      );

    case 'terms':
      return <TermsBody form={form} errors={errors} update={update} clearErr={clearErr} />;

    case 'review':
      return <ReviewBody form={form} errors={errors} update={update} clearErr={clearErr} />;

    default:
      return null;
  }
}

// --- repeatable sub-forms ---
interface SubProps {
  form: ApplicationForm;
  errors: Errors;
  update: (p: Patch) => void;
  clearErr: (k: string) => void;
}

function rowInput(
  value: string,
  onChange: (v: string) => void,
  errKey: string,
  errors: Errors,
  label: string,
  opts: {
    width?: number;
    type?: string;
    placeholder?: string;
    required?: boolean;
    options?: any[];
    disabled?: boolean;
  } = {}
) {
  const err = errors[errKey];
  return (
    <div className={`apply-f${opts.width ? ' w' + opts.width : ''}`}>
      <label>
        {label}
        {opts.required && <span className="apply-req"> *</span>}
      </label>
      {opts.options ? (
        <select
          value={value}
          className={err ? 'bad' : ''}
          disabled={opts.disabled}
          onChange={(e) => onChange(e.target.value)}
        >
          {opts.options.map((o: any) => (
            <option key={o.value ?? o} value={o.value ?? o}>
              {o.label ?? o}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={opts.type || 'text'}
          value={value}
          placeholder={opts.placeholder}
          className={err ? 'bad' : ''}
          autoComplete="off"
          disabled={opts.disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {err && <div className="apply-err">{err}</div>}
    </div>
  );
}

// The address a "same as previous" location copies: the main office for the
// first location, the prior location otherwise.
function LocationsBody({ form, errors, update, clearErr }: SubProps) {
  const setLoc = (i: number, patch: any) => {
    const locations = form.locations.map((l, idx) => (idx === i ? { ...l, ...patch } : l));
    update({ locations });
  };
  return (
    <div className="apply-rows">
      {form.locations.map((l, i) => {
        // A mirrored row shows (and submits) its source address live, so editing
        // the office or a prior location never leaves a stale copy behind.
        const mirror = effectiveLocationAddress(form, i);
        return (
          <div className="apply-row" key={i}>
            <div className="apply-row__h">
              <strong>Location {i + 1}</strong>
              {form.locations.length > 1 && (
                <button
                  type="button"
                  className="apply-btn apply-btn--ghost"
                  onClick={() => update(removeLocationAt(form, i))}
                >
                  Remove
                </button>
              )}
            </div>
            <label className="apply-check" style={{ marginBottom: 4 }}>
              <input
                type="checkbox"
                checked={l.sameAsPrevious}
                onChange={(e) => {
                  setLoc(i, { sameAsPrevious: e.target.checked });
                  if (e.target.checked)
                    ['line1', 'city', 'state', 'zip'].forEach((k) =>
                      clearErr(`locations.${i}.${k}`)
                    );
                }}
              />
              <span>{sameAsPreviousLabel(i)}</span>
            </label>
            <div className="apply-fields">
              {rowInput(
                l.name,
                (v) => setLoc(i, { name: v }),
                `locations.${i}.name`,
                errors,
                'Location name',
                { placeholder: 'Main yard' }
              )}
              {l.sameAsPrevious ? (
                rowInput(mirror.line1, () => {}, `locations.${i}.line1`, errors, 'Street address', {
                  required: true,
                  width: 4,
                  disabled: true,
                })
              ) : (
                <AutoLine1
                  label="Street address"
                  required
                  width={4}
                  value={l.line1}
                  error={errors[`locations.${i}.line1`]}
                  onText={(v) => {
                    setLoc(i, { line1: v });
                    clearErr(`locations.${i}.line1`);
                  }}
                  onPlace={(addr) => {
                    setLoc(i, {
                      line1: addr.line1,
                      city: addr.city,
                      state: addr.state,
                      zip: addr.postalCode,
                      // Google returns the unit as subpremise; fill it when present.
                      line2: addr.line2,
                    });
                    ['line1', 'city', 'state', 'zip'].forEach((k) =>
                      clearErr(`locations.${i}.${k}`)
                    );
                  }}
                />
              )}
              {rowInput(
                l.sameAsPrevious ? mirror.line2 : l.line2,
                (v) => setLoc(i, { line2: v }),
                `locations.${i}.line2`,
                errors,
                'Suite or unit',
                { width: 2, disabled: l.sameAsPrevious }
              )}
              {rowInput(
                l.sameAsPrevious ? mirror.city : l.city,
                (v) => {
                  setLoc(i, { city: v });
                  clearErr(`locations.${i}.city`);
                },
                `locations.${i}.city`,
                errors,
                'City',
                { required: true, width: 3, disabled: l.sameAsPrevious }
              )}
              {rowInput(
                l.sameAsPrevious ? mirror.state : l.state,
                (v) => {
                  setLoc(i, { state: v });
                  clearErr(`locations.${i}.state`);
                },
                `locations.${i}.state`,
                errors,
                'State',
                {
                  required: true,
                  width: 1,
                  disabled: l.sameAsPrevious,
                  options: [
                    { value: '', label: '—' },
                    ...US_STATES.map((s) => ({ value: s, label: s })),
                  ],
                }
              )}
              {rowInput(
                l.sameAsPrevious ? mirror.zip : l.zip,
                (v) => {
                  setLoc(i, { zip: v });
                  clearErr(`locations.${i}.zip`);
                },
                `locations.${i}.zip`,
                errors,
                'ZIP',
                { required: true, width: 2, disabled: l.sameAsPrevious }
              )}
              {rowInput(
                l.deliveryNotes,
                (v) => setLoc(i, { deliveryNotes: v }),
                `locations.${i}.notes`,
                errors,
                'Delivery notes',
                { placeholder: 'Gate hours, truck access, where to dump' }
              )}
            </div>
          </div>
        );
      })}
      <div className="apply-row-actions">
        <button
          type="button"
          className="apply-btn"
          onClick={() => update({ locations: [...form.locations, blankLocation()] })}
        >
          + Add another location
        </button>
      </div>
    </div>
  );
}

function PrincipalsBody({ form, errors, update, clearErr }: SubProps) {
  const prepay = isPrepay(form);
  const ownership = totalOwnershipPercent(form.principals);
  const ownershipOff = Math.abs(ownership - 100) > 0.01;
  const setP = (i: number, patch: any) =>
    update({ principals: form.principals.map((p, idx) => (idx === i ? { ...p, ...patch } : p)) });
  const ownerName = `${form.ownerFirstName} ${form.ownerLastName}`.trim();
  return (
    <div className="apply-rows">
      {form.principals.map((pr, i) => {
        // Prefill from the account-owner details entered earlier. Derived checked
        // state (no extra form state); editing the name/title unchecks it.
        const isOwnerPrincipal =
          !!ownerName && pr.fullName === ownerName && pr.title === form.ownerTitle;
        return (
          <div className="apply-row" key={i}>
            <div className="apply-row__h">
              <strong>Principal {i + 1}</strong>
              {form.principals.length > 1 && (
                <button
                  type="button"
                  className="apply-btn apply-btn--ghost"
                  onClick={() => {
                    // signerIndex points into principals[] — remap it so removing a
                    // row doesn't silently re-point the signer to a different owner.
                    const signer = Number(form.signerIndex);
                    let signerIndex = form.signerIndex;
                    if (form.signerIndex !== '' && Number.isFinite(signer)) {
                      if (signer === i) signerIndex = '';
                      else if (signer > i) signerIndex = String(signer - 1);
                    }
                    update({
                      principals: form.principals.filter((_, idx) => idx !== i),
                      signerIndex,
                    });
                    clearErr('principalsTotal');
                  }}
                >
                  Remove
                </button>
              )}
            </div>
            <label className="apply-check" style={{ marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={isOwnerPrincipal}
                onChange={(ev) => {
                  if (ev.target.checked) {
                    setP(i, { fullName: ownerName, title: form.ownerTitle });
                    clearErr(`principals.${i}.fullName`);
                  } else {
                    setP(i, { fullName: '', title: '' });
                  }
                }}
              />
              <span>This is me — use my name and title</span>
            </label>
            <div className="apply-fields">
              {rowInput(
                pr.fullName,
                (v) => {
                  setP(i, { fullName: v });
                  clearErr(`principals.${i}.fullName`);
                },
                `principals.${i}.fullName`,
                errors,
                'Full name',
                { required: true, width: 3 }
              )}
              {rowInput(
                pr.title,
                (v) => setP(i, { title: v }),
                `principals.${i}.title`,
                errors,
                'Title',
                { width: 2 }
              )}
              {rowInput(
                pr.percentOwned,
                (v) => {
                  setP(i, { percentOwned: v });
                  clearErr(`principals.${i}.pct`);
                  clearErr('principalsTotal');
                },
                `principals.${i}.pct`,
                errors,
                '% owned',
                { type: 'number', width: 1 }
              )}
              <AutoLine1
                label="Home address"
                required
                width={4}
                value={pr.homeLine1}
                error={errors[`principals.${i}.homeLine1`]}
                onText={(v) => {
                  setP(i, { homeLine1: v });
                  clearErr(`principals.${i}.homeLine1`);
                }}
                onPlace={(addr) => {
                  setP(i, {
                    homeLine1: addr.line1,
                    homeCity: addr.city,
                    homeState: addr.state,
                    homeZip: addr.postalCode,
                    // Google returns the unit as subpremise; fill it when present.
                    homeLine2: addr.line2,
                  });
                  ['homeLine1', 'homeCity', 'homeState', 'homeZip'].forEach((k) =>
                    clearErr(`principals.${i}.${k}`)
                  );
                }}
              />
              {rowInput(
                pr.homeLine2,
                (v) => setP(i, { homeLine2: v }),
                `principals.${i}.homeLine2`,
                errors,
                'Apt or unit',
                { width: 2 }
              )}
              {rowInput(
                pr.homeCity,
                (v) => {
                  setP(i, { homeCity: v });
                  clearErr(`principals.${i}.homeCity`);
                },
                `principals.${i}.homeCity`,
                errors,
                'City',
                { required: true, width: 3 }
              )}
              {rowInput(
                pr.homeState,
                (v) => {
                  setP(i, { homeState: v });
                  clearErr(`principals.${i}.homeState`);
                },
                `principals.${i}.homeState`,
                errors,
                'State',
                {
                  required: true,
                  width: 1,
                  options: [
                    { value: '', label: '—' },
                    ...US_STATES.map((s) => ({ value: s, label: s })),
                  ],
                }
              )}
              {rowInput(
                pr.homeZip,
                (v) => {
                  setP(i, { homeZip: v });
                  clearErr(`principals.${i}.homeZip`);
                },
                `principals.${i}.homeZip`,
                errors,
                'ZIP',
                { required: true, width: 2 }
              )}
              {!prepay && (
                <div className="apply-f w3">
                  <label>
                    Social Security number{!pr.ssnOnFile && <span className="apply-req"> *</span>}
                  </label>
                  <input
                    type="password"
                    value={pr.ssn}
                    placeholder={pr.ssnOnFile ? `•••-••-${pr.ssnLast4} on file` : ''}
                    className={errors[`principals.${i}.ssn`] ? 'bad' : ''}
                    autoComplete="off"
                    onChange={(e) => {
                      setP(i, { ssn: e.target.value });
                      clearErr(`principals.${i}.ssn`);
                    }}
                  />
                  <span className="apply-lock">
                    {pr.ssnOnFile
                      ? `On file (•••-••-${pr.ssnLast4}). Leave blank to keep, or re-enter to change.`
                      : 'Encrypted. Used only for the credit check you authorize.'}
                  </span>
                  {errors[`principals.${i}.ssn`] && (
                    <div className="apply-err">{errors[`principals.${i}.ssn`]}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
      <div className="apply-row-actions">
        <button
          type="button"
          className="apply-btn"
          onClick={() => {
            update({ principals: [...form.principals, blankPrincipal()] });
            clearErr('principalsTotal');
          }}
        >
          + Add principal
        </button>
        <span
          className={ownershipOff ? 'apply-err' : 'apply-muted'}
          style={{ alignSelf: 'center' }}
        >
          Total ownership: {ownership}% of 100%
        </span>
      </div>
      {errors.principalsTotal && <div className="apply-err">{errors.principalsTotal}</div>}
    </div>
  );
}

function ReferencesBody({
  form,
  errors,
  update,
  clearErr,
  onFile,
}: SubProps & { onFile: (f: File) => void }) {
  const setR = (i: number, patch: any) =>
    update({ references: form.references.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) });
  const attached = Boolean(form.tradeReferenceFileName);
  return (
    <div className="apply-rows">
      {/* "See attached" — upload a reference sheet instead of filling all three. */}
      <div className="apply-attach">
        <div>
          {attached ? (
            <span>
              <b>✓ Attached:</b> {form.tradeReferenceFileName}
              <span className="apply-muted"> — the references below are now optional.</span>
            </span>
          ) : (
            <span className="apply-muted">
              Have a reference sheet already? Attach it instead of filling in all three.
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <label className="apply-btn apply-btn--ghost" htmlFor="f_traderef">
            {attached ? 'Replace file' : 'See attached'}
          </label>
          {attached && (
            <button
              type="button"
              className="apply-btn apply-btn--ghost"
              onClick={() => update({ tradeReferenceFileName: '' })}
            >
              Remove
            </button>
          )}
          <input
            id="f_traderef"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            hidden
            onChange={(ev) => {
              const file = ev.target.files?.[0];
              if (file) onFile(file);
            }}
          />
        </div>
      </div>
      {form.references.map((r, i) => (
        <div className="apply-row" key={i}>
          <div className="apply-row__h">
            <strong>Reference {i + 1}</strong>
          </div>
          <div className="apply-fields">
            {rowInput(
              r.companyName,
              (v) => {
                setR(i, { companyName: v });
                clearErr(`references.${i}.companyName`);
              },
              `references.${i}.companyName`,
              errors,
              'Company name',
              { required: true, width: 3 }
            )}
            {rowInput(
              r.contactName,
              (v) => setR(i, { contactName: v }),
              `references.${i}.contactName`,
              errors,
              'Contact',
              { width: 3 }
            )}
            {rowInput(
              r.phone,
              (v) => {
                setR(i, { phone: v });
                clearErr(`references.${i}.phone`);
              },
              `references.${i}.phone`,
              errors,
              'Phone',
              { required: true, type: 'tel', width: 3 }
            )}
            {rowInput(
              r.email,
              (v) => {
                setR(i, { email: v });
                clearErr(`references.${i}.email`);
              },
              `references.${i}.email`,
              errors,
              'Email',
              { required: true, type: 'email', width: 3 }
            )}
            {rowInput(
              r.line1,
              (v) => setR(i, { line1: v }),
              `references.${i}.line1`,
              errors,
              'Address'
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function TeamBody({ form, errors, update, clearErr }: SubProps) {
  const setM = (i: number, patch: any) =>
    update({ teamMembers: form.teamMembers.map((m, idx) => (idx === i ? { ...m, ...patch } : m)) });
  return (
    <div className="apply-rows">
      {!form.teamMembers.length && (
        <div className="apply-note">
          No team members yet. You can skip this and add people later.
        </div>
      )}
      {form.teamMembers.map((m, i) => (
        <div className="apply-row" key={i}>
          <div className="apply-row__h">
            <strong>Team member {i + 1}</strong>
            <button
              type="button"
              className="apply-btn apply-btn--ghost"
              onClick={() =>
                update({ teamMembers: form.teamMembers.filter((_, idx) => idx !== i) })
              }
            >
              Remove
            </button>
          </div>
          <div className="apply-fields">
            {rowInput(
              m.name,
              (v) => {
                setM(i, { name: v });
                clearErr(`teamMembers.${i}.name`);
              },
              `teamMembers.${i}.name`,
              errors,
              'Name',
              { required: true, width: 3 }
            )}
            {rowInput(
              m.email,
              (v) => {
                setM(i, { email: v });
                clearErr(`teamMembers.${i}.email`);
              },
              `teamMembers.${i}.email`,
              errors,
              'Email',
              { required: true, type: 'email', width: 3 }
            )}
            {rowInput(
              m.role,
              (v) => setM(i, { role: v }),
              `teamMembers.${i}.role`,
              errors,
              'What can they do?',
              { options: TEAM_ROLES }
            )}
          </div>
          {form.locations.length > 0 && (
            <div className="apply-f" style={{ maxWidth: 320 }}>
              <label>Which locations can they see?</label>
              <MultiSelectDropdown
                label="Locations"
                placeholder="All locations"
                options={form.locations.map((l, li) => ({
                  value: String(li),
                  label: l.name || `Location ${li + 1}`,
                }))}
                value={m.locationAccess.map(String)}
                onChange={(next) => setM(i, { locationAccess: next.map((n) => Number(n)) })}
              />
            </div>
          )}
        </div>
      ))}
      <div className="apply-row-actions">
        <button
          type="button"
          className="apply-btn"
          onClick={() => update({ teamMembers: [...form.teamMembers, blankTeamMember()] })}
        >
          + Add team member
        </button>
      </div>
    </div>
  );
}

function ProductsBody({
  form,
  errors,
  update,
  clearErr,
  products,
}: SubProps & { products: Array<{ id: string; name: string }> }) {
  const [search, setSearch] = useState('');
  const selected = form.productInterestIds;

  const toggle = (id: string, on: boolean) => {
    update({
      productInterestIds: on ? [...selected, id] : selected.filter((x) => x !== id),
    });
    clearErr('products');
  };

  const term = search.trim().toLowerCase();
  // Filter the list by name, but never hide an already-selected product — a
  // search shouldn't make the user's own picks seem to vanish.
  const visible = term
    ? products.filter((pr) => pr.name.toLowerCase().includes(term) || selected.includes(pr.id))
    : products;
  const hiddenSelectedCount = term
    ? selected.filter((id) => {
        const pr = products.find((x) => x.id === id);
        return pr && !pr.name.toLowerCase().includes(term);
      }).length
    : 0;

  return (
    <>
      {products.length > 0 && (
        <div className="apply-prods__toolbar">
          <input
            type="search"
            className="apply-prods__search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products…"
            aria-label="Search products"
          />
          <div className="apply-prods__meta">
            <span className="apply-muted">{selected.length} selected</span>
            {selected.length > 0 && (
              <button
                type="button"
                className="apply-link"
                onClick={() => {
                  update({ productInterestIds: [] });
                  clearErr('products');
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      <div className="apply-prods">
        {visible.map((pr) => {
          const on = selected.includes(pr.id);
          return (
            <label key={pr.id} className={`apply-prod${on ? ' on' : ''}`}>
              <input
                type="checkbox"
                checked={on}
                onChange={(ev) => toggle(pr.id, ev.target.checked)}
              />
              <span>{pr.name}</span>
            </label>
          );
        })}
        {!products.length && <p className="apply-muted">No products available yet.</p>}
        {products.length > 0 && !visible.length && (
          <p className="apply-muted">No products match “{search.trim()}”.</p>
        )}
      </div>

      {hiddenSelectedCount > 0 && (
        <p className="apply-muted apply-prods__hidden-note">
          {hiddenSelectedCount} selected product{hiddenSelectedCount === 1 ? '' : 's'} hidden by
          your search.
        </p>
      )}

      {errors.products && <div className="apply-err">{errors.products}</div>}
    </>
  );
}

const TERMS_1 =
  'I hereby certify that all statements contained in this application are true and made for the purposes of obtaining credit. In consideration of DeFeo Materials LLC selling to me or my agent(s), I agree to: (1) maintain the account on NET 30 DAYS terms; (2) pay service charges for late payment at 18% PER ANNUM; (3) pay all reasonable collection and attorney fees if the account is placed in collection; (4) authorize any credit investigation needed and personally guarantee the debt; (5) a 1.5% late fee added after 7 days past due, and an account hold after 30 days unpaid; (6) allow DeFeo Materials LLC to run my credit.';
const TERMS_2 =
  'Customer waives its rights to notice and hearing under the prejudgment remedies provided for in Connecticut General Statutes, Chapter 903A, as amended, for any default of this credit agreement. This credit application must be executed by a principal listed above or a duly authorized agent, who personally guarantees and agrees to be personally liable for all of Buyer’s obligations, including timely payment of all sums due. The personal guarantee also applies if the Buyer declares or applies for bankruptcy protection.';

function TermsBody({ form, errors, update, clearErr }: SubProps) {
  const prepay = isPrepay(form);
  const signerOptions = form.principals
    .map((p, i) => ({ value: String(i), label: p.fullName }))
    .filter((o) => o.label);

  const SignBlock = (
    <div className="apply-fields" style={{ marginTop: 12 }}>
      <div className="apply-f w3">
        <label>
          Signing as (owner/officer)<span className="apply-req"> *</span>
        </label>
        <select
          value={form.signerIndex}
          className={errors.signerIndex ? 'bad' : ''}
          onChange={(e) => {
            const idx = e.target.value;
            // Carry over the title entered for that principal, so it isn't retyped.
            const picked = form.principals[Number(idx)];
            update({ signerIndex: idx, ...(picked?.title ? { signerTitle: picked.title } : {}) });
            clearErr('signerIndex');
            if (picked?.title) clearErr('signerTitle');
          }}
        >
          <option value="">Choose…</option>
          {signerOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {errors.signerIndex && <div className="apply-err">{errors.signerIndex}</div>}
      </div>
      <div className="apply-f w3">
        <label>
          Title<span className="apply-req"> *</span>
        </label>
        <input
          value={form.signerTitle}
          className={errors.signerTitle ? 'bad' : ''}
          onChange={(e) => {
            update({ signerTitle: e.target.value });
            clearErr('signerTitle');
          }}
        />
        {errors.signerTitle && <div className="apply-err">{errors.signerTitle}</div>}
      </div>
      <div className="apply-f">
        <label>
          Type your full name to sign<span className="apply-req"> *</span>
        </label>
        <input
          style={{ fontStyle: 'italic' }}
          value={form.signerTypedSignature}
          className={errors.signerTypedSignature ? 'bad' : ''}
          onChange={(e) => {
            update({ signerTypedSignature: e.target.value });
            clearErr('signerTypedSignature');
          }}
        />
        {errors.signerTypedSignature && (
          <div className="apply-err">{errors.signerTypedSignature}</div>
        )}
      </div>
      <span className="apply-lock" style={{ gridColumn: 'span 6' }}>
        Must be signed by a principal listed on this application. We record the signer, date, time
        and IP address.
      </span>
    </div>
  );

  if (prepay) {
    return (
      <>
        <div className="apply-banner apply-banner--warn">
          <b>Prepay account.</b> You chose prepay, so there's no credit line and the credit terms
          don't apply. Orders are paid before delivery. You can apply for credit later.
        </div>
        <label className="apply-check" style={{ marginTop: 12 }}>
          <input
            type="checkbox"
            checked={form.agreedToTerms}
            onChange={(e) => {
              update({ agreedToTerms: e.target.checked });
              clearErr('agreedToTerms');
            }}
          />
          <span>
            I certify the information in this application is true and I'm authorized to open this
            account for <b>{form.legalName || 'this business'}</b>. I understand orders must be paid
            before delivery.
          </span>
        </label>
        {errors.agreedToTerms && <div className="apply-err">{errors.agreedToTerms}</div>}
        {SignBlock}
      </>
    );
  }

  return (
    <>
      <div className="apply-terms" tabIndex={0}>
        <b>DeFeo Materials LLC Credit Terms</b>
        <p>{TERMS_1}</p>
        <p>
          <b>{TERMS_2}</b>
        </p>
      </div>
      <label className="apply-check" style={{ marginTop: 12 }}>
        <input
          type="checkbox"
          checked={form.agreedToTerms}
          onChange={(e) => {
            update({ agreedToTerms: e.target.checked });
            clearErr('agreedToTerms');
          }}
        />
        <span>
          I certify this application is true and agree to the terms above for{' '}
          <b>{form.legalName || 'this business'}</b>, including Net 30 terms, late charges, the
          credit check, and the Connecticut prejudgment-remedy waiver.
        </span>
      </label>
      {errors.agreedToTerms && <div className="apply-err">{errors.agreedToTerms}</div>}
      <label className="apply-check" style={{ marginTop: 10 }}>
        <input
          type="checkbox"
          checked={form.agreedPersonalGuarantee}
          onChange={(e) => {
            update({ agreedPersonalGuarantee: e.target.checked });
            clearErr('agreedPersonalGuarantee');
          }}
        />
        <span>
          I personally guarantee this account's debt, including if the business files for
          bankruptcy.
        </span>
      </label>
      {errors.agreedPersonalGuarantee && (
        <div className="apply-err">{errors.agreedPersonalGuarantee}</div>
      )}
      {SignBlock}
    </>
  );
}

function ReviewBody({ form, errors, update, clearErr }: SubProps) {
  const prepay = isPrepay(form);
  const Row = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="apply-rv">
      <b>{title}</b>
      <p>{children}</p>
    </div>
  );
  return (
    <>
      <div className="apply-review">
        <Row title="Business">
          {form.legalName}
          {form.dba ? ` dba ${form.dba}` : ''} · EIN {form.ein}
        </Row>
        <Row title="Account owner">
          {form.ownerFirstName} {form.ownerLastName} · {form.ownerEmail}
        </Row>
        <Row title="Delivery locations">
          {form.locations.map((l) => l.name || `${l.city}, ${l.state}`).join(' · ')}
        </Row>
        <Row title="Credit">
          {prepay ? 'Prepay (no credit)' : `Requesting ${creditLineLabel(form.creditLine)}`} ·{' '}
          {form.annualTonsRange} per year
        </Row>
        <Row title="Products">{form.productInterestIds.length} selected</Row>
        <Row title="Signed">
          {form.signerTypedSignature || '—'}
          {form.signerTitle ? `, ${form.signerTitle}` : ''}
        </Row>
      </div>
      <div className="apply-captcha">
        <label className="apply-check">
          <input
            type="checkbox"
            checked={form.notRobot}
            onChange={(e) => {
              update({ notRobot: e.target.checked });
              clearErr('notRobot');
            }}
          />
          <span>I'm not a robot</span>
        </label>
        <small className="apply-muted">Protected by reCAPTCHA</small>
      </div>
      {errors.notRobot && <div className="apply-err">{errors.notRobot}</div>}
    </>
  );
}
